import { useState, useEffect } from 'react'
import { NavBar, Card, Loading, Empty, Tag, Steps, Tabs, Toast } from 'react-vant'
import { ArrowLeft, CalendarO } from '@react-vant/icons'
import { useNavigate } from 'react-router-dom'
import schedulingService from '../../api/scheduling'
import request from '../../api/request'

export default function MyTimetable() {
    const navigate = useNavigate()
    const [loading, setLoading] = useState(true)
    const [timetable, setTimetable] = useState([])

    // 获取今天周几 (1-7)
    const today = new Date().getDay() || 7
    const [activeDay, setActiveDay] = useState(today.toString())

    useEffect(() => {
        const fetchTimetable = async () => {
            setLoading(true)
            try {
                // 动态获取当前学年（替代硬编码 '2025-2026'）
                const yearRes = await request.get('/academic-year/current/')
                const currentYear = yearRes.current_academic_year || yearRes.data?.current_academic_year || ''
                const res = await schedulingService.getMyTimetable(currentYear)
                setTimetable(res || [])
            } catch (e) {
                console.error('获取课表失败:', e)
            } finally {
                setLoading(false)
            }
        }
        fetchTimetable()
    }, [])

    // 将课表数据按照周几进行分组
    const groupedTimetable = (timetable || []).reduce((acc, curr) => {
        const day = curr.time_slot.day_of_week
        if (!acc[day]) acc[day] = []
        acc[day].push(curr)
        return acc
    }, {})

    // 固定展示周一到周五
    const workdayKeys = ['1', '2', '3', '4', '5']
    const dayNames = {
        1: '周一', 2: '周二', 3: '周三', 4: '周四', 5: '周五', 6: '周六', 7: '周日'
    }

    // 检查是否有周末课，如果有则也展示
    const hasWeekend = Object.keys(groupedTimetable).some(d => d === '6' || d === '7')
    const displayDays = hasWeekend ? ['1', '2', '3', '4', '5', '6', '7'] : workdayKeys

    return (
        <div style={{ background: '#f8fafc', minHeight: '100vh', paddingBottom: 20 }}>
            <NavBar
                title="我的课表"
                leftText="返回"
                leftArrow={<ArrowLeft />}
                onClickLeft={() => navigate(-1)}
                fixed
                placeholder
            />

            {loading ? (
                <div style={{ padding: '100px 0', textAlign: 'center' }}>
                    <Loading type="ball" color="#00b578">加载中...</Loading>
                </div>
            ) : (timetable && timetable.length === 0) ? (
                <div style={{ marginTop: 100 }}>
                    <Empty description="暂无排课信息" />
                </div>
            ) : (
                <div style={{ marginTop: 0 }}>
                    <div style={{
                        background: '#fff',
                        padding: '16px 16px 12px',
                        position: 'sticky',
                        top: 46,
                        zIndex: 99,
                        boxShadow: '0 2px 10px rgba(0,0,0,0.02)'
                    }}>
                        {/* 自定义分段控制器 (Segmented Control) */}
                        <div style={{
                            background: '#f1f5f9',
                            borderRadius: 12,
                            padding: 4,
                            display: 'flex',
                            position: 'relative'
                        }}>
                            {/* 滑动背景块 (简单模拟版，可以通过 activeDay 计算位移) */}
                            {displayDays.map(day => (
                                <div
                                    key={day}
                                    onClick={() => setActiveDay(day)}
                                    style={{
                                        flex: 1,
                                        height: 34,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 14,
                                        fontWeight: activeDay === day ? 700 : 500,
                                        color: activeDay === day ? '#0f172a' : '#475569',
                                        background: activeDay === day ? '#fff' : 'transparent',
                                        borderRadius: 9,
                                        boxShadow: activeDay === day ? '0 2px 8px rgba(0,0,0,0.12)' : 'none',
                                        transition: 'all 0.25s ease',
                                        zIndex: 1,
                                        cursor: 'pointer',
                                        margin: '0 1px'
                                    }}
                                >
                                    {dayNames[day]}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div style={{ padding: '0 12px 12px' }}>
                        {displayDays.map(day => activeDay === day && (
                            <div key={day} style={{ marginTop: 12 }}>
                                {!groupedTimetable[day] || groupedTimetable[day].length === 0 ? (
                                    <div style={{ padding: '60px 0' }}>
                                        <Empty imageSize={80} description={`${dayNames[day]}暂无排课`} />
                                    </div>
                                ) : (
                                    <Card round style={{ overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.06)', border: 'none' }}>
                                        <div style={{
                                            padding: '16px 16px',
                                            background: 'linear-gradient(135deg, #f0f9f4 0%, #ffffff 100%)',
                                            borderBottom: '1px solid #f0fdf4',
                                            fontSize: 14,
                                            color: '#64748b',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between'
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <div style={{ width: 4, height: 16, background: '#00b578', borderRadius: 2, marginRight: 10 }}></div>
                                                <span style={{ fontWeight: 600 }}>课程安排</span>
                                            </div>
                                            <span style={{ fontSize: 13, color: '#94a3b8' }}>
                                                今日共 <span style={{ color: '#00b578', fontWeight: 700 }}>{groupedTimetable[day].length}</span> 节
                                            </span>
                                        </div>
                                        <div style={{ padding: '20px 16px 12px' }}>
                                            <Steps direction="vertical" active={99} activeColor="#00b578">
                                                {groupedTimetable[day]
                                                    .sort((a, b) => a.time_slot.period_index - b.time_slot.period_index)
                                                    .map(item => (
                                                        <Steps.Item key={item.id} renderIcon={() => (
                                                            <div style={{
                                                                width: 26, height: 26, borderRadius: '8px 8px 0 8px',
                                                                background: '#00b578', color: '#fff',
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                fontSize: 13, fontWeight: 700,
                                                                boxShadow: '0 4px 8px rgba(0,181,120,0.25)'
                                                            }}>
                                                                {item.time_slot.period_index}
                                                            </div>
                                                        )}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f8fafc' }}>
                                                                <div style={{ flex: 1 }}>
                                                                    <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', letterSpacing: '0.02em' }}>
                                                                        {item.subject?.name}
                                                                    </div>
                                                                    <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 6, display: 'flex', alignItems: 'center' }}>
                                                                        <span style={{ background: '#f8fafc', padding: '2px 6px', borderRadius: 4 }}>{item.class_info?.class_name || '未知班级'}</span>
                                                                        <span style={{ margin: '0 8px', color: '#dcdee0' }}>|</span>
                                                                        <span>第 {item.time_slot.period_index} 节</span>
                                                                    </div>
                                                                </div>

                                                                {item.is_locked && (
                                                                    <Tag color="#f0fdf4" textColor="#056b4f" round size="medium" style={{ padding: '2px 10px' }}>固定</Tag>
                                                                )}
                                                            </div>
                                                        </Steps.Item>
                                                    ))}
                                            </Steps>
                                        </div>
                                    </Card>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}


        </div>
    )
}
